// web/src/scene/rtls/HeatmapLayer.tsx
//
// Heatmap renderer — paints vertex colors on the loaded map model geometry.
// Replaces the webview's eventBus-driven version with Redux selectors.
//
// Key differences from the webview port:
//   - Driven by Redux state (heatmapEnabled, heatmapType, positionHistory) instead of eventBus.
//   - Mine extents derived from positionHistory with hard-coded fallback.
//   - 1 s debounce on cell recomputation to avoid thrash on rapid type-switch.
//   - HeatmapCell.x/y are world-space (from computeHeatmap); grid indices computed here.
//   - Grid indexing is x-major (gx * GRID_SIZE + gy) matching the webview exactly.

import { useEffect, useMemo, useState, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { useAppSelector } from '../../app/useAppSelector'
import { computeHeatmap } from '@shared/services/heatmapService'
import {
  findMapModel,
  paintModelVertexColorsAsync,
  restoreOriginals,
  type OriginalMeshData,
} from '../shared/rampPainter'
import type { HeatmapCell } from '@shared/types/rtlsTypes'

const GRID_SIZE = 50

// Fallback extents when positionHistory is empty. Matches the BoxedMine seed.
const FALLBACK_EXTENTS = {
  x: [8402, 9717] as [number, number],
  y: [5594, 6504] as [number, number],
  z: [0, 0] as [number, number],
}

// Yellow (#F59E0B) → Red (#DC2626) ramp, matching the webview. intensity: 0..1.
function intensityToColor(intensity: number, target: THREE.Color): THREE.Color {
  return target.setRGB(
    0.961 + intensity * (0.863 - 0.961),
    0.620 + intensity * (0.149 - 0.620),
    0.043 + intensity * (0.149 - 0.043),
  )
}

// HeatmapCell.x/y are world-space coordinates (computeHeatmap outputs world centers).
// Convert to grid indices here — x-major layout (gx * GRID_SIZE + gy), matching webview.
function buildIntensityGrid(
  cells: HeatmapCell[],
  xMin: number,
  xStep: number,
  yMin: number,
  yStep: number,
): Float32Array {
  const grid = new Float32Array(GRID_SIZE * GRID_SIZE)
  for (const cell of cells) {
    const gx = Math.floor((cell.x - xMin) / xStep)
    const gy = Math.floor((cell.y - yMin) / yStep)
    if (gx >= 0 && gx < GRID_SIZE && gy >= 0 && gy < GRID_SIZE) {
      grid[gx * GRID_SIZE + gy] = Math.max(grid[gx * GRID_SIZE + gy], cell.intensity)
    }
  }
  return grid
}

// Bilinear sample at world-space (worldX, worldY). Returns 0..1.
// Uses x-major indexing (gx * GRID_SIZE + gy) and a 0.5-cell offset, matching the webview.
function sampleIntensity(
  grid: Float32Array,
  worldX: number,
  worldY: number,
  xMin: number,
  xStep: number,
  yMin: number,
  yStep: number,
): number {
  const fx = (worldX - xMin) / xStep - 0.5
  const fy = (worldY - yMin) / yStep - 0.5

  const gx0 = Math.max(0, Math.min(GRID_SIZE - 1, Math.floor(fx)))
  const gy0 = Math.max(0, Math.min(GRID_SIZE - 1, Math.floor(fy)))
  const gx1 = Math.min(GRID_SIZE - 1, gx0 + 1)
  const gy1 = Math.min(GRID_SIZE - 1, gy0 + 1)

  const tx = fx - gx0
  const ty = fy - gy0

  const v00 = grid[gx0 * GRID_SIZE + gy0]
  const v10 = grid[gx1 * GRID_SIZE + gy0]
  const v01 = grid[gx0 * GRID_SIZE + gy1]
  const v11 = grid[gx1 * GRID_SIZE + gy1]

  return (
    v00 * (1 - tx) * (1 - ty) +
    v10 * tx * (1 - ty) +
    v01 * (1 - tx) * ty +
    v11 * tx * ty
  )
}

export const HeatmapLayer: React.FC = () => {
  const { scene } = useThree()
  const heatmapEnabled  = useAppSelector(s => s.rtls.heatmapEnabled)
  const heatmapType     = useAppSelector(s => s.rtls.heatmapType)
  const positionHistory = useAppSelector(s => s.rtls.positionHistory)
  const sceneReady      = useAppSelector(s => s.viewer.sceneReady)
  // modelInfo flips from null → non-null when the GLB finishes loading.
  // Including it in the paint effect's deps ensures a retry if the user
  // toggles heatmap before the 26 MB map finished downloading.
  const modelInfo       = useAppSelector(s => s.map.modelInfo)

  // TRUE originals — saved once on first paint, never overwritten on type-switch
  const trueOriginals = useRef<OriginalMeshData[]>([])
  const isApplied     = useRef(false)

  // Currently-running paint abort, so the disable-branch can cancel it before
  // calling restoreOriginals. Without this, an in-flight paint completes AFTER
  // the restore and overwrites the just-restored materials.
  const currentAbort = useRef<AbortController | null>(null)

  // Derive mine extents from positionHistory; fall back to hard-coded seed extents.
  const extents = useMemo(() => {
    let minX = Infinity, maxX = -Infinity
    let minY = Infinity, maxY = -Infinity
    for (const entries of Object.values(positionHistory)) {
      for (const e of entries) {
        if (e.position.x < minX) minX = e.position.x
        if (e.position.x > maxX) maxX = e.position.x
        if (e.position.y < minY) minY = e.position.y
        if (e.position.y > maxY) maxY = e.position.y
      }
    }
    if (!Number.isFinite(minX)) return FALLBACK_EXTENTS
    const pad = 50
    return {
      x: [minX - pad, maxX + pad] as [number, number],
      y: [minY - pad, maxY + pad] as [number, number],
      z: [0, 0] as [number, number],
    }
  }, [positionHistory])

  // Debounced heatmap cells — 1 s to avoid thrash on rapid type-switch.
  const [debouncedCells, setDebouncedCells] = useState<HeatmapCell[]>([])
  useEffect(() => {
    if (!heatmapEnabled) {
      setDebouncedCells([])
      return
    }
    const t = setTimeout(() => {
      const cells = computeHeatmap(positionHistory, heatmapType, extents)
      setDebouncedCells(cells)
    }, 1000)
    return () => clearTimeout(t)
  }, [heatmapEnabled, heatmapType, positionHistory, extents])

  // Paint / restore effect.
  useEffect(() => {
    if (!heatmapEnabled || debouncedCells.length === 0) {
      // Abort any in-flight paint before restoring — otherwise the pending
      // .then() branch below runs AFTER restoreOriginals and overwrites the
      // just-restored materials. Reproduction: toggle ON, wait ~200 ms, toggle
      // OFF while the chunked paint is still walking the geometry.
      if (currentAbort.current) {
        currentAbort.current.abort()
        currentAbort.current = null
      }
      if (isApplied.current) {
        restoreOriginals(trueOriginals.current)
        trueOriginals.current = []
        isApplied.current = false
      }
      return
    }

    const mapModel = findMapModel(scene)
    if (!mapModel) {
      if (import.meta.env.DEV) {
        console.warn(
          '[HeatmapLayer] no map model in scene yet — heatmap will retry ' +
          'when mapSlice.modelInfo updates (GLB finishes loading).',
        )
      }
      return
    }

    const xMin  = extents.x[0]
    const xStep = (extents.x[1] - extents.x[0]) / GRID_SIZE
    const yMin  = extents.y[0]
    const yStep = (extents.y[1] - extents.y[0]) / GRID_SIZE

    const grid = buildIntensityGrid(debouncedCells, xMin, xStep, yMin, yStep)
    const tempColor = new THREE.Color()
    const abort = new AbortController()
    currentAbort.current = abort

    // Paint on top of existing state — no restore before async paint (avoids blink)
    paintModelVertexColorsAsync(
      mapModel,
      (worldX, worldY) => {
        const intensity = sampleIntensity(grid, worldX, worldY, xMin, xStep, yMin, yStep)
        if (intensity > 0.05) {
          intensityToColor(intensity, tempColor)
          return { r: tempColor.r, g: tempColor.g, b: tempColor.b }
        }
        return null
      },
      abort.signal,
    )
      .then(originals => {
        if (abort.signal.aborted) return
        if (originals.length === 0) return

        // Keep the FIRST (pre-heatmap) originals; dispose intermediate clones
        // produced by rapid type-switching.
        if (trueOriginals.current.length === 0) {
          trueOriginals.current = originals
        } else {
          for (const { material } of originals) {
            if (Array.isArray(material)) {
              material.forEach(m => m?.dispose?.())
            } else {
              ;(material as THREE.Material)?.dispose?.()
            }
          }
        }
        isApplied.current = true
      })
      .catch(err => {
        if (!abort.signal.aborted) {
          console.error('[HeatmapLayer] paint failed:', err)
        }
      })

    return () => {
      abort.abort()
    }
    // sceneReady is included so a late map load re-triggers the paint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCells, heatmapEnabled, scene, extents, sceneReady, modelInfo])

  // Cleanup on unmount — restore geometry to unmodified state.
  useEffect(() => {
    return () => {
      if (isApplied.current) {
        restoreOriginals(trueOriginals.current)
        trueOriginals.current = []
        isApplied.current = false
      }
    }
  }, [])

  return null
}

HeatmapLayer.displayName = 'HeatmapLayer'
